import { CSSProperties, ReactElement, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getObjectFromQuerystring } from "utilities/BrowserUtility";
import { GlobalContext } from "./GlobalContext";
import { applyGlobalFilterData } from "utilities/MiscUtilities";
import SortableTable, { IColumnDefinition } from "./SortableTable";
import { Button } from "react-bootstrap";
import { DEFAULT_TABLE_RECORD_COUNT } from "utilities/UIUtilities";

interface ICommonLookupDisplayProps<U, I, O> {
    dataTypeName: string;
    dataTypeNamePlural: string;
    pathName: string;
    baseFilters: U;
    filter: U;
    setFilter: (filters: U) => void;
    filterHints: any;
    service: any;
    statesFilterKey?: string;
    countriesFilterKey?: string;
    columnDefinitions: IColumnDefinition<I, U>[];
    primaryIdKey: keyof I;
    className?: string;
    rowStyleRenderer?: (rowData: I) => CSSProperties;
    rowClassRenderer?: (rowData: I) => string;
    extraPreRowsRenderer?: () => ReactElement[];
    tools?: ReactElement[];
    forceRefresh?: Date;
    forceReload?: Date;
}

// U = Filters, I = PrimaryModel, O = create model
const CommonLookupDisplay = <U, I, O>(props: ICommonLookupDisplayProps<U, I, O>) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const getSearchParams = () => ({
        ...props.baseFilters as U,
        ...getObjectFromQuerystring(
            searchParams,
            props.filterHints)
    });

    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [refresh, setRefresh] = useState<Date>(new Date());
    const [primaryData, setPrimaryData] = useState<I[]>([]);
    const navigate = useNavigate();
    const { page = 1 }: any = useParams();
    const [totalRecords, setTotalRecords] = useState(0);
    const [lastGlobalFilterUpdateValue, setLastGlobalFilterUpdateValue] = useState<Date>();

    useEffect(
        () => {
            (async () => {
                globalContextRef.current.setLoading(true);
                let currentFilter = props.filter;

                if (JSON.stringify(currentFilter) == "{}") {
                    currentFilter = getSearchParams();
                    props.setFilter(currentFilter);
                    return;
                }

                const accessToken = await globalContext.getAccessTokenAsync();
                const result =
                    await props.service.GetAllAsync(
                        accessToken,
                        page - 1,
                        DEFAULT_TABLE_RECORD_COUNT,
                        applyGlobalFilterData(
                            props.statesFilterKey ?? "",
                            props.countriesFilterKey ?? "",
                            currentFilter,
                            globalContextRef.current.region,
                            globalContextRef.current.organizationType));

                if (result.records != null) {
                    setPrimaryData(result.records);
                    setTotalRecords(result.total);
                }
                else {
                    globalContextRef.current.showErrorMessage(`Unable to load ${props.dataTypeNamePlural}`);
                }

                globalContextRef.current.setLoading(false);
            })();
        }, [page, props.filter, refresh, props.forceReload]);

    useEffect(() => {
        if (globalContextRef.current.forceUpdateGlobalFilters != lastGlobalFilterUpdateValue) {
            setLastGlobalFilterUpdateValue(globalContextRef.current.forceUpdateGlobalFilters);
            console.log("Forcing update of global filters " + globalContextRef.current.forceUpdateGlobalFilters);
            changePage(0);
            setRefresh(new Date());
        }
    }, [globalContextRef.current.forceUpdateGlobalFilters]);

    useEffect(() => {
        setPrimaryData([...primaryData]);
    }, [props.forceRefresh]);

    const changePage = (newPage: number) => {
        navigate({
            pathname: `/${props.pathName}/${newPage + 1}`,
            search: window.location.search
        });
    };

    const updateSearchParams = (newParams: any) => {
        setSearchParams(newParams);
        props.setFilter(newParams);
        changePage(0);
    };

    return (
        <>
            <SortableTable<I, U>
                changePageCallback={changePage}
                page={page}
                className={`lookup-table ${props.className}`}
                totalRecords={totalRecords}
                records={primaryData}
                recordCount={primaryData.length}
                setFilter={updateSearchParams}
                filter={props.filter}
                rowClickCallback={(data) => navigate(`/${props.pathName}/Detail/${data[props.primaryIdKey]}`)}
                tools={[
                    <Button
                        className="me-2 full-sm"
                        key="resetfilter"
                        onClick={() => { updateSearchParams({}); }}>Reset Filters</Button>,
                    ...(props.tools || [])
                ]}
                columnDefinitions={props.columnDefinitions}
                rowClassRenderer={props.rowClassRenderer}
                rowStyleRenderer={props.rowStyleRenderer}
                extraPreRowsRenderer={props.extraPreRowsRenderer}
            />
        </>
    );
}

export default CommonLookupDisplay;
